import React, { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { TbArrowBackUp, TbDeviceFloppy } from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import { useThemeManager } from "../hooks/useThemeManager";
import { useCss } from "../context/CssContext";
import ThemeSection from "./ThemeSection";
import SuccessAlert from "./alerts/SuccessAlert";
import ErrorAlert from "./alerts/ErrorAlert";
import config from "../config";
import "./CssPanel.css";

const CssPanelNew = ({ open, onClose }) => {
  const { themeValues, themeConfig, flatConfig, updateThemeValue, resetToDefaults } =
    useThemeManager();
  const { fetchAndApplyCss } = useCss();
  
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Alert mesajlarını otomatik temizle
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  if (!open) return null;

  // CSS verilerini API'ye kaydet
  const saveCssToApi = async () => {
    setLoading(true);
    
    try {
      const cssData = {};

      // Sadece değiştirilmiş CSS değerlerini ekle
      Object.keys(flatConfig).forEach((key) => {
        const config = flatConfig[key];
        if (config && config.cssVar) {
          cssData[config.cssVar] = themeValues[key];
        }
      });

      const response = await fetch(`${config.adminApi}/set_css`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({data:  cssData}),
        mode: 'cors',
        credentials: 'include',
      });

      const result = await response.json();

      if (response.ok && result.status) {
        setSuccessMessage(result.message || "CSS ayarları başarıyla kaydedildi!");
        setErrorMessage('');
        
        // CSS kaydedildikten sonra yeni CSS'leri yükle
        await fetchAndApplyCss();
      } else {
        setErrorMessage(result.message || 'CSS kaydetme hatası oluştu');
        setSuccessMessage('');
      }
    } catch (error) {
      console.error('CSS kaydetme hatası:', error);
      setErrorMessage("Bağlantı hatası! Lütfen tekrar deneyin.");
      setSuccessMessage('');
    } finally {
      setLoading(false);
      setShowSaveModal(false);
    }
  };

  // Varsayılan ayarlara sıfırla (onaylı)
  const handleConfirmedReset = () => {
    resetToDefaults();
    setShowResetModal(false);
    setSuccessMessage("Ayarlar varsayılan değerlere sıfırlandı!");
    setErrorMessage('');
  };

  // Bölüm başlıkları
  const sectionTitles = {
    general: "Genel Ayarlar",
    header: "Header Ayarları",
    mainCategory: "Ana Kategori Ayarları",
    tab: "Tab Ayarları",
    bonusButtons: "Bonus Kart Butonları",
    rulesPopup: "Kurallar Popup'ı",
    historyPopup: "Geçmiş Popup'ı",
    multipleBonusDialog: "Çoklu Bonus Dialog'u",
    bonusCard: "Bonus Kart'ı",
    bonusConfirmPopup: "Bonus Onay Popup'ı",
    loadingPage: "Loading Sayfası Ayarları",
  };

  return (
    <div className="css-panel">
      {/* Alert Mesajları */}
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <ErrorAlert message={errorMessage} />}
      
      <div className="css-panel-header">
        <h2>CSS Düzenleme Paneli</h2>
        <div className="header-actions">
          <button
            className="save-button"
            onClick={() => setShowSaveModal(true)}
            disabled={loading}
            title="CSS ayarlarını kaydet"
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#22c55e' }} />
            ) : (
              <TbDeviceFloppy size={20} />
            )}
            CSS Kaydet
          </button>
          <button
            className="reset-button"
            onClick={() => setShowResetModal(true)}
            disabled={loading}
            title="Varsayılan ayarlara dön"
          >
            <TbArrowBackUp size={20} />
            Sıfırla
          </button>
          <button className="close-button" onClick={onClose}>
            <IoMdClose size={24} />
          </button>
        </div>
      </div>

      <div className="css-panel-content">
        {Object.entries(themeConfig).map(([sectionKey, properties]) => (
          <ThemeSection
            key={sectionKey}
            title={sectionTitles[sectionKey]}
            sectionKey={sectionKey}
            properties={properties}
            themeValues={themeValues}
            onThemeChange={updateThemeValue}
            defaultExpanded={sectionKey === "general"}
          />
        ))}
      </div>

      {/* CSS Kaydet Onay Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>CSS Ayarlarını Kaydet</h3>
            </div>
            <div className="modal-content">
              <p>Mevcut CSS ayarlarını kaydetmek istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                Bu işlem sunucudaki CSS ayarlarını güncelleyecektir.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowSaveModal(false)}
                disabled={loading}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button"
                onClick={saveCssToApi}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                    Kaydediliyor...
                  </>
                ) : (
                  'Evet, Kaydet'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Varsayılana Sıfırla Onay Modal */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Varsayılan Ayarlara Sıfırla</h3>
            </div>
            <div className="modal-content">
              <p>Tüm CSS ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#e74c3c', marginTop: '8px' }}>
                ⚠️ Bu işlem geri alınamaz! Mevcut ayarlarınız kaybolacaktır.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowResetModal(false)}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button reset"
                onClick={handleConfirmedReset}
              >
                Evet, Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CssPanelNew;
